import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Column,
  ExpandedState,
  Header,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
  useTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import _ from 'lodash';
import type { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';

import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import NoDataToDisplay from 'components/V5Gadget/NoDataToDisplay';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import ReportContext from 'contexts/ReportContext';

import {
  buildApiShim,
  buildColumnShim,
  buildInertBridge,
  buildNodeShim,
  ColumnShim,
  ContextMenuRequest,
  getColumnField,
  NodeShim,
  RowEventBus,
  ShimColumnState,
  ShimExportParams,
  ShimSortModel,
  sortingFromSortModel,
  sortModelFromSorting,
  TableBridge,
} from './agParamsShim';
import applyColumnLayout, {
  ColumnLayout,
  EMPTY_COLUMN_LAYOUT,
} from './applyColumnLayout';
import CellEditorPopup from './CellEditorPopup';
import ColumnDragGhost from './ColumnDragGhost';
import DataTableContextMenu, { MenuEntry } from './DataTableContextMenu';
import downloadTableCsv from './downloadTableCsv';
import buildExportCells, { toCsv } from './exportCells';
import exportTableExcel from './exportTableExcel';
import { DATA_TABLE_FEATURES, DataTableFeatures } from './features';
import fitColumnsToWidth from './fitColumnsToWidth';
import fromAgColDefs, { withFallbackIds } from './fromAgColDefs';
import DefaultHeader from './DefaultHeader';
import useBodyFillHeight from './useBodyFillHeight';
import useColumnMoveAnimation from './useColumnMoveAnimation';
import useContainerWidth from './useContainerWidth';
import useGridNavigation from './useGridNavigation';
import useHeadlessHeight from './useHeadlessHeight';
import {
  CellContent,
  Expander,
  Overlay,
  Scroller,
  Table,
} from './DataTable.styles';

type AnyFn = (...args: any[]) => any;
type TableRow = Row<DataTableFeatures, DataTable.Row>;
type TableColumn = Column<DataTableFeatures, DataTable.Row>;
type TableHeader = Header<DataTableFeatures, DataTable.Row, unknown>;

// How far from the end of the loaded rows a virtualised table asks for more
const END_REACHED_THRESHOLD = 20;
const OVERSCAN = 10;
// Past this many rows a scrolling table draws only the rows in view, as AG
// does for every grid: a matrix showing all its terms can have thousands
const AUTO_VIRTUALISE_ROW_COUNT = 150;
// jsdom has no layout, so give the virtualiser a window to fill in tests
const TEST_RECT = { width: 1200, height: 800 };

const EMPTY_ROWS: DataTable.Row[] = [];
// Added to a content-sized column's measured width, as AG's autoSizePadding
const AUTO_SIZE_PADDING = 20;
// Frames to wait for a table mounted out of view to be laid out
const MAX_MEASURE_ATTEMPTS = 20;
// fixed: a column without a width of its own, as AG's default colDef width
const DEFAULT_FIXED_COLUMN_WIDTH = 200;
// The narrowest a column can be dragged to
const MIN_RESIZED_COLUMN_WIDTH = 40;
// AG's right-click menu on a grid without getContextMenuItems, less Cut and
// Paste (always disabled: nothing is editable that way)
const DEFAULT_CONTEXT_MENU_ITEMS: DataTable.ContextMenuItem[] = [
  'copy',
  'copyWithHeaders',
  'copyWithGroupHeaders',
  'separator',
  'export',
];
// AG's file name for an export from its menu
const MENU_EXPORT_FILE_NAME = 'export';

type RowEdit = {
  // The owner's row this edit was made to. When the owner hands over a new
  // object for the row (it saved the edit, or reloaded), the edit is dropped
  base: DataTable.Row;
  data: DataTable.Row;
};

type EditingCell = { rowId: string; colId: string; anchor: HTMLElement };

type ColumnEvent = 'columnMoved' | 'columnVisible' | 'columnResized';

// The file name AG would write: its own extension isn't added twice
// A header being dragged (isColumnMovable): the column (or group) it moves,
// the order it'd leave the columns in, drawn while dragging, and where the
// drag began, for its ghost
type ColumnDrag = {
  columnId: string;
  label: string;
  // Dragged off the table: let go, it hides the column
  isOutside: boolean;
  previewOrder?: string[];
  start: { x: number; y: number };
};

// The leaf ids with a column's (or group's) leaves moved before or after
// another's
const moveColumnBlock = ({
  ids,
  movedIds,
  targetIds,
  side,
}: {
  ids: string[];
  movedIds: string[];
  targetIds: string[];
  side: 'before' | 'after';
}): string[] => {
  const rest = ids.filter((id) => !movedIds.includes(id));
  rest.splice(
    side === 'after'
      ? rest.indexOf(targetIds[targetIds.length - 1]) + 1
      : rest.indexOf(targetIds[0]),
    0,
    ...movedIds,
  );
  return rest;
};

// How far a header moves before it's a drag rather than a click
const COLUMN_DRAG_THRESHOLD = 5;
// How far above or below the table a dragged header hides its column
const HIDE_DRAG_DISTANCE = 40;

const withoutExtension = ({
  fileName,
  extension,
}: {
  fileName: string;
  extension: string;
}) =>
  fileName.toLowerCase().endsWith(`.${extension}`)
    ? fileName.slice(0, -(extension.length + 1))
    : fileName;

const toCellText = (value: unknown) =>
  value === undefined || value === null ? '' : `${value}`;

const writeToClipboard = (text: string) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => undefined);
  }
};

const isLeafColumn = (column: TableColumn): boolean =>
  !column.columns || column.columns.length === 0;

const metaOf = (column: TableColumn): DataTable.ColumnMeta => {
  const meta: DataTable.ColumnMeta | undefined = column.columnDef.meta;
  if (!meta) {
    throw new Error(`DataTable: column ${column.id} has no meta`);
  }
  return meta;
};

const resolveCellStyle = ({
  colDef,
  params,
}: {
  colDef: ColDef;
  params: { [key: string]: any };
}): CSSProperties | undefined => {
  const { cellStyle } = colDef;
  if (!cellStyle) {
    return undefined;
  }
  if (typeof cellStyle === 'function') {
    const fn: AnyFn = cellStyle;
    return fn(params) ?? undefined;
  }
  const style: CSSProperties = cellStyle;
  return style;
};

type CellParamsInput = {
  meta: DataTable.ColumnMeta;
  value: unknown;
  data: DataTable.Row;
  node: NodeShim;
  column: ColumnShim | undefined;
  api: ReturnType<typeof buildApiShim>;
  context: { [key: string]: any };
  rowIndex: number;
};

// AG's ICellRendererParams for a cell, as the renderers read them
const buildCellParams = ({
  meta,
  value,
  data,
  node,
  column,
  api,
  context,
  rowIndex,
}: CellParamsInput) => ({
  ...meta.cellRendererParams,
  value,
  valueFormatted: meta.valueFormatter
    ? meta.valueFormatter(value, data)
    : undefined,
  data,
  colDef: meta.colDef,
  column,
  node,
  api,
  context,
  rowIndex,
});

// A cell's content, drawn again only when what it's drawn from changes. The
// table re-renders as it scrolls and resizes (the virtualiser follows the
// scroller), and that mustn't re-render every visible cell's renderer
// (tooltips, icons, links) when nothing in them changed; AG doesn't redraw
// its cells then either. refreshKey changes when AG would refresh the cells
// (a sort, the row's selection).
const MemoCellContent = React.memo(function MemoCellContent(
  props: CellParamsInput & { refreshKey: unknown; isSelected: boolean },
) {
  const Component = props.meta.cellComponent;
  const params = buildCellParams(props);
  return Component ? (
    <Component {...params} />
  ) : (
    <span>{`${params.valueFormatted ?? params.value ?? ''}`}</span>
  );
});

// What every cell is drawn with besides its own row and column
type CellEnv = {
  getNode: (args: {
    rowId: string;
    data: DataTable.Row;
    rowIndex: number;
    isPinned: boolean;
  }) => NodeShim;
  columnShims: Map<string, ColumnShim>;
  api: ReturnType<typeof buildApiShim>;
  context: { [key: string]: any };
  rowHeight: number;
  // The column with the expand toggle, when rows have sub-rows
  expanderColumnId: string | undefined;
  // Changes when AG would refresh the cells (a sort, redrawRows)
  refreshKey: unknown;
};

const renderCellContent = ({
  env,
  column,
  data,
  rowId,
  rowIndex,
  isPinned,
  value,
  tableRow,
}: {
  env: CellEnv;
  column: TableColumn;
  data: DataTable.Row;
  rowId: string;
  rowIndex: number;
  isPinned: boolean;
  value: unknown;
  tableRow?: TableRow;
}) => {
  const meta = metaOf(column);
  const paramsInput: CellParamsInput = {
    meta,
    value,
    data,
    node: env.getNode({ rowId, data, rowIndex, isPinned }),
    column: env.columnShims.get(column.id),
    api: env.api,
    context: env.context,
    rowIndex,
  };
  const isExpanderCell = !!tableRow && column.id === env.expanderColumnId;

  return (
    <CellContent
      // AG's row height includes the row's bottom border
      height={env.rowHeight - 1}
      style={{
        ...(meta.colDef.cellStyle
          ? resolveCellStyle({
              colDef: meta.colDef,
              params: buildCellParams(paramsInput),
            })
          : undefined),
        paddingLeft: tableRow && tableRow.depth > 0 ? tableRow.depth * 16 : 0,
      }}
    >
      {isExpanderCell && tableRow && tableRow.getCanExpand() && (
        <Expander
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            tableRow.toggleExpanded();
          }}
          aria-label={tableRow.getIsExpanded() ? 'Collapse' : 'Expand'}
          aria-expanded={tableRow.getIsExpanded()}
        >
          {tableRow.getIsExpanded() ? '▾' : '▸'}
        </Expander>
      )}
      <MemoCellContent
        {...paramsInput}
        refreshKey={env.refreshKey}
        isSelected={!!tableRow && tableRow.getIsSelected()}
      />
    </CellContent>
  );
};

// fitContent: an empty column takes the width the others leave, before any
// right-pinned columns, as AG lays out a fitted grid
const insertFiller = ({
  cells,
  columnIds,
  filler,
  fillerBeforeId,
}: {
  cells: React.ReactNode[];
  columnIds: string[];
  filler: React.ReactNode;
  fillerBeforeId: string | undefined;
}) => {
  const index = fillerBeforeId ? columnIds.indexOf(fillerBeforeId) : -1;
  return index === -1
    ? [...cells, filler]
    : [...cells.slice(0, index), filler, ...cells.slice(index)];
};

// A body row, drawn again only when something it shows changes: its data,
// position, selection or expansion, the columns' widths, the sort or the
// focused cell. The table re-renders as its context changes (a resize
// updates the row height and report contexts), which mustn't redraw every
// row when none of that changed.
const BodyRow = React.memo(function BodyRow({
  tableRow,
  displayIndex,
  isSelected,
  isExpanded,
  headerRowCount,
  env,
  columnStyles,
  leafIndexes,
  getCellProps,
  hasFiller,
  fillerBeforeId,
  events,
  onRowClicked,
  className,
}: {
  tableRow: TableRow;
  displayIndex: number;
  // Read from the row, but passed so a change re-renders it
  isSelected: boolean;
  isExpanded: boolean;
  // From the grid's rowClassRules
  className: string | undefined;
  headerRowCount: number;
  env: CellEnv;
  columnStyles: Map<string, CSSProperties>;
  leafIndexes: Map<string, number>;
  getCellProps: ReturnType<typeof useGridNavigation>['getCellProps'];
  hasFiller: boolean;
  fillerBeforeId: string | undefined;
  events: RowEventBus;
  onRowClicked?: (data: DataTable.Row) => void;
}) {
  const rowId = tableRow.id;
  const data = tableRow.original;
  const visibleCells = tableRow.getVisibleCells();
  const cells = visibleCells.map((cell) => {
    const meta = metaOf(cell.column);
    return (
      <td
        key={cell.id}
        role="gridcell"
        data-col-id={cell.column.id}
        title={
          meta.tooltipValueGetter
            ? meta.tooltipValueGetter(cell.getValue(), data)
            : undefined
        }
        {...getCellProps({
          row: displayIndex + 1,
          col: leafIndexes.get(cell.column.id) ?? 0,
        })}
        className={[
          meta.cellClass,
          meta.pinned ? `dt-pinned dt-pinned-${meta.pinned}` : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={columnStyles.get(cell.column.id)}
      >
        {renderCellContent({
          env,
          column: cell.column,
          data,
          rowId,
          rowIndex: tableRow.index,
          isPinned: false,
          value: cell.getValue(),
          tableRow,
        })}
      </td>
    );
  });
  const classNames = [
    'dt-row',
    displayIndex % 2 === 1 ? 'dt-odd' : undefined,
    isSelected ? 'dt-selected' : undefined,
    className,
  ].filter(Boolean);
  return (
    <tr
      role="row"
      data-row-id={rowId}
      className={classNames.join(' ')}
      // Where the row shows, counting the header rows, as AG numbers them
      aria-rowindex={headerRowCount + displayIndex + 1}
      aria-selected={isSelected}
      aria-expanded={tableRow.getCanExpand() ? isExpanded : undefined}
      onMouseEnter={() => events.emit({ rowId, event: 'mouseEnter' })}
      onMouseLeave={() => events.emit({ rowId, event: 'mouseLeave' })}
      onClick={onRowClicked ? () => onRowClicked(data) : undefined}
    >
      {hasFiller
        ? insertFiller({
            cells,
            columnIds: visibleCells.map((cell) => cell.column.id),
            filler: (
              <td
                key="dt-filler"
                className="dt-filler"
                role="presentation"
                aria-hidden
              />
            ),
            fillerBeforeId,
          })
        : cells}
    </tr>
  );
});

const DataTable = ({
  columnDefs,
  defaultColDef,
  components,
  rows,
  totalsRows = EMPTY_ROWS,
  context,
  gridOptions,
  sizing,
  fittedColumnIds,
  isFirstColumnFitted,
  isManualSorting,
  onSortChanged,
  isRowSelected,
  getSubRows,
  isVirtualised: isVirtualisedProp,
  onEndReached,
  isLoading,
  noDataToDisplay,
  isHeadlessHeightEnabled,
  onExportHandle,
  totalRowCount,
  onRowClicked,
  tableId,
  testId = 'data-table',
  skipHeaderWhenSizing,
  layout = 'fill',
  scrollToColumnId,
  hasDarkHeader,
  isResizable,
  hasBorders,
  isSearchScreen,
  getRowId,
  getContextMenuItems,
  onGridReady,
  isRefitPaused,
  isColumnMovable = true,
  onCellClicked,
  onColumnMoved,
  onColumnVisible,
  isExpandedByDefault,
  isParentRowExported,
}: {
  columnDefs: (ColDef | ColGroupDef)[];
  defaultColDef?: ColDef;
  // AG's component registry, for colDefs that name their renderers
  components?: { [key: string]: React.ComponentType<any> };
  rows: DataTable.Row[];
  totalsRows?: DataTable.Row[];
  // AG's gridOptions.context, as the renderers expect it
  context?: { [key: string]: any };
  // Read by renderers through api.getGridOption
  gridOptions?: { [key: string]: any };
  sizing: DataTable.SizingMode;
  // fitWidth only: these columns are as wide as their content and the
  // others share the rest (AG's autoSizeColumns on a flex grid)
  fittedColumnIds?: string[];
  isFirstColumnFitted?: boolean;
  // The rows arrive sorted (by the server); sorting only reports changes
  isManualSorting?: boolean;
  onSortChanged?: (sortModel: ShimSortModel) => void;
  isRowSelected?: (data: DataTable.Row) => boolean;
  getSubRows?: (data: DataTable.Row) => DataTable.Row[] | undefined;
  // Draw only the rows in view. Defaults to on for a table that scrolls
  // (fill layout, not the PDF) with more than AUTO_VIRTUALISE_ROW_COUNT rows
  isVirtualised?: boolean;
  onEndReached?: () => void;
  isLoading?: boolean;
  noDataToDisplay?: boolean;
  // Report the natural height under the scheduled-report PDF route
  isHeadlessHeightEnabled?: boolean;
  onExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
  totalRowCount?: number;
  onRowClicked?: (data: DataTable.Row) => void;
  tableId?: string;
  testId?: string;
  // fitContent: size the columns by their cells only, letting headers wrap
  // (AG's autoSizeAllColumns(skipHeader))
  skipHeaderWhenSizing?: boolean;
  // fill: scroll inside the container (the default). natural: as tall as the
  // rows, with no inner vertical scroll (AG's domLayout autoHeight)
  layout?: 'fill' | 'natural';
  // Scroll sideways to show this column once the widths are settled, and
  // again whenever it changes (AG's ensureColumnVisible)
  scrollToColumnId?: string;
  // The gadget header colour behind the column headers (AG's hasDarkHeader)
  hasDarkHeader?: boolean;
  // Columns can be dragged wider or narrower from their header's right edge,
  // for as long as the table is shown (AG's resizable columns)
  isResizable?: boolean;
  // A border round the table (AG's hasBorders)
  hasBorders?: boolean;
  // The Search screen's larger text (AG's ag-theme-fleetops-search)
  isSearchScreen?: boolean;
  // A row's id, which stays the same however the rows are sorted or paged
  // (AG's getRowId), given the row and its position in `rows`. Selection and
  // edits follow it. Defaults to the position
  getRowId?: (data: DataTable.Row, index: number) => string;
  // The right-click menu's items for a cell (AG's getContextMenuItems).
  // Without it, the menu has AG's Copy, Copy with Headers and Export
  getContextMenuItems?: (params: {
    node: NodeShim;
    column: ColumnShim | null;
    value: unknown;
    api: ReturnType<typeof buildApiShim>;
    context: { [key: string]: any };
    defaultItems: DataTable.ContextMenuItem[];
  }) => DataTable.ContextMenuItem[];
  // Hands the owner the table's api once it mounts (AG's onGridReady), for
  // owners written against AG's GridApi. It's the shim, which covers the
  // calls those owners make (events, forEachNode, setGridOption('context'),
  // redrawRows, ensureNodeVisible...); any other call fails loudly outside
  // production
  onGridReady?: (event: { api: GridApi }) => void;
  // fitWidth with column limits: keep the current widths while set, rather
  // than refitting as the container resizes (a slide-out laid over the
  // table)
  isRefitPaused?: boolean;
  // Columns (and column groups) can be dragged by their header to a new
  // place, or off the table to hide them, unless their colDef's
  // suppressMovable says not. On by default, as AG's movable columns are
  // (false: AG's suppressMovableColumns)
  isColumnMovable?: boolean;
  // AG's onCellClicked / onColumnMoved / onColumnVisible, called with AG's
  // event objects (loosely typed: handlers written for AG's event types read
  // the fields the shim's events carry)
  onCellClicked?: (event: any) => void;
  onColumnMoved?: (event: any) => void;
  onColumnVisible?: (event: any) => void;
  // getSubRows: every row starts expanded (a printed table, where nothing
  // can be clicked open), not collapsed
  isExpandedByDefault?: boolean;
  // getSubRows: exports write each parent row, then its sub-rows, rather
  // than the sub-rows alone (parents carrying values of their own:
  // subtotals)
  isParentRowExported?: boolean;
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const { isPdfExport } = useContext(ReportContext);
  const scrollerLayout =
    isHeadlessHeightEnabled && isPdfExport ? 'print' : layout;
  const isVirtualised =
    isVirtualisedProp ??
    (scrollerLayout === 'fill' && rows.length > AUTO_VIRTUALISE_ROW_COUNT);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tableElementRef = useRef<HTMLTableElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const headerCellRefs = useRef(new Map<string, HTMLTableCellElement>());
  // Column group headers, for dragging columns past them
  const groupHeaderRefs = useRef(new Map<string, HTMLTableCellElement>());
  const nodeCache = useRef(new Map<string, NodeShim>());

  // One bridge for the table's lifetime; its functions are refreshed below on
  // every render so the shims always read live state
  const bridgeRef = useRef<TableBridge>(buildInertBridge());
  const bridge = bridgeRef.current;
  Object.assign(bridge.context, context);
  bridge.gridOptions = gridOptions ?? {};

  // A row's id: the owner's (getRowId), else its position in `rows`
  const getRowIdRef = useRef(getRowId);
  getRowIdRef.current = getRowId;
  const rowIdOf = useCallback(
    (data: DataTable.Row, index: number) =>
      getRowIdRef.current ? getRowIdRef.current(data, index) : `${index}`,
    [],
  );
  const tableGetRowId = useMemo(
    () =>
      getRowId
        ? (data: DataTable.Row, index: number) => getRowId(data, index)
        : undefined,
    [getRowId],
  );
  const rowsById = useMemo(() => {
    const byId = new Map<string, DataTable.Row>();
    rows.forEach((data, index) => byId.set(rowIdOf(data, index), data));
    return byId;
  }, [rowIdOf, rows]);
  const rowsByIdRef = useRef(rowsById);
  rowsByIdRef.current = rowsById;

  // Rows edited in the table (a cell editor, node.updateData or
  // setDataValue), drawn in place of the owner's row until the owner hands
  // over a new object for it, as AG's node.data holds the edit
  const [rowEdits, setRowEdits] = useState<Map<string, RowEdit>>(
    () => new Map(),
  );
  const shownRows = useMemo(() => {
    if (rowEdits.size === 0) {
      return rows;
    }
    return rows.map((data, index) => {
      const edit = rowEdits.get(rowIdOf(data, index));
      return edit && edit.base === data ? edit.data : data;
    });
  }, [rowEdits, rowIdOf, rows]);
  bridge.updateRowData = ({ rowId, change }) =>
    setRowEdits((current) => {
      const base = rowsByIdRef.current.get(rowId);
      if (!base) {
        return current;
      }
      const previous = current.get(rowId);
      const from = previous && previous.base === base ? previous.data : base;
      const next = new Map(current);
      next.set(rowId, { base, data: change(from) });
      return next;
    });

  // AG's redrawRows: rows draw again, with the row classes and cells their
  // context now gives
  const [redrawKey, setRedrawKey] = useState<number>(0);
  bridge.redrawRows = () => setRedrawKey((key) => key + 1);
  bridge.setContext = (value) => {
    Object.assign(bridge.context, value);
    setRedrawKey((key) => key + 1);
  };

  // Columns moved, shown or hidden (dragged, or through applyColumnState),
  // over the definitions. Read synchronously by applyColumnState, which may
  // be called several times before the table re-renders
  const [columnLayout, setColumnLayout] =
    useState<ColumnLayout>(EMPTY_COLUMN_LAYOUT);
  const columnLayoutRef = useRef<ColumnLayout>(columnLayout);
  const changeColumnLayout = useCallback((layout: ColumnLayout) => {
    columnLayoutRef.current = layout;
    setColumnLayout(layout);
  }, []);
  // An owner's own defaultColDef (setGridOption('defaultColDef')), in place
  // of the one passed
  const [ownDefaultColDef, setOwnDefaultColDef] = useState<
    ColDef | undefined
  >();
  const effectiveDefaultColDef = ownDefaultColDef ?? defaultColDef;
  bridge.getDefaultColDef = () => effectiveDefaultColDef;
  bridge.setDefaultColDef = setOwnDefaultColDef;

  // A header being dragged; its preview order is drawn until it's let go
  const [columnDrag, setColumnDrag] = useState<ColumnDrag | undefined>();
  const columnDefsWithIds = useMemo(
    () => withFallbackIds(columnDefs),
    [columnDefs],
  );
  const previewOrder = columnDrag?.previewOrder;
  const laidOutColumnDefs = useMemo(
    () =>
      applyColumnLayout({
        columnDefs: columnDefsWithIds,
        layout: previewOrder
          ? { ...columnLayout, order: previewOrder }
          : columnLayout,
      }),
    [columnDefsWithIds, columnLayout, previewOrder],
  );
  const { snapshot: snapshotColumns } = useColumnMoveAnimation({
    tableRef: tableElementRef,
    orderKey: (previewOrder ?? columnLayout.order ?? []).join('|'),
  });
  const { columns, initialSorting } = useMemo(
    () =>
      fromAgColDefs({
        columnDefs: laidOutColumnDefs,
        defaultColDef: effectiveDefaultColDef,
        components,
        bridge,
      }),
    [bridge, components, effectiveDefaultColDef, laidOutColumnDefs],
  );

  // AG's grid events, to the api's listeners and to the owner's handler
  // props. The props are read through a ref, current on every render, rather
  // than subscribed in an effect, so an event straight after the table draws
  // still reaches them
  const eventHandlersRef = useRef({
    cellClicked: onCellClicked,
    columnMoved: onColumnMoved,
    columnVisible: onColumnVisible,
  });
  eventHandlersRef.current = {
    cellClicked: onCellClicked,
    columnMoved: onColumnMoved,
    columnVisible: onColumnVisible,
  };
  const emitTableEvent = useCallback(
    (
      event: 'cellClicked' | ColumnEvent,
      details: { [key: string]: unknown },
    ) => {
      bridge.tableEvents.emit(event, details);
      const handler =
        event === 'columnResized' ? undefined : eventHandlersRef.current[event];
      if (handler) {
        handler({ ...details, type: event, api: bridge.tableEvents.getApi() });
      }
    },
    [bridge],
  );

  // Column events (AG's columnMoved, columnVisible, columnResized) go out
  // once the change is drawn, so listeners reading the column state see it
  const pendingColumnEventsRef = useRef<Set<ColumnEvent>>(new Set());
  useEffect(() => {
    const pending = pendingColumnEventsRef.current;
    if (pending.size === 0) {
      return;
    }
    pendingColumnEventsRef.current = new Set();
    pending.forEach((event) =>
      emitTableEvent(event, { finished: true, source: 'api' }),
    );
  });
  useEffect(() => {
    bridge.tableEvents.emit('columnEverythingChanged');
  }, [bridge, columns]);

  // Sorting. AG re-applies a colDef's `sort` whenever new colDefs arrive, so
  // do the same: reset to the defs' sort whenever it differs
  const initialSortingKey = JSON.stringify(initialSorting);
  const defsSorting = useMemo(
    (): SortingState => JSON.parse(initialSortingKey),
    [initialSortingKey],
  );
  const [sorting, setSorting] = useState<SortingState>(defsSorting);
  // Read synchronously by the column shims: a header that sorts and then
  // immediately asks for its column's sort must see the new value
  const sortingRef = useRef<SortingState>(sorting);
  useEffect(() => {
    sortingRef.current = defsSorting;
    setSorting(defsSorting);
    bridge.tableEvents.emit('sortChanged');
  }, [bridge, defsSorting]);

  const onSortChangedRef = useRef(onSortChanged);
  onSortChangedRef.current = onSortChanged;
  const onSortingChange = useCallback((updater: Updater<SortingState>) => {
    const next =
      typeof updater === 'function' ? updater(sortingRef.current) : updater;
    sortingRef.current = next;
    setSorting(next);
    bridgeRef.current.tableEvents.emit('sortChanged');
    if (onSortChangedRef.current) {
      onSortChangedRef.current(sortModelFromSorting(next));
    }
  }, []);

  // AG's `hide`: hidden columns still sort and export, but don't show
  const columnVisibility = useMemo(() => {
    const visibility: { [columnId: string]: boolean } = {};
    const visit = (list: typeof columns) =>
      list.forEach((c) => {
        if (c.columns) {
          visit(c.columns);
        } else if (c.meta.isHidden) {
          visibility[c.id] = false;
        }
      });
    visit(columns);
    return visibility;
  }, [columns]);

  // Selection: derived from the rows when the owner decides it (matrix drill
  // highlight), otherwise whatever renderers select
  const [ownSelection, setOwnSelection] = useState<RowSelectionState>({});
  const rowSelection = useMemo((): RowSelectionState => {
    if (!isRowSelected) {
      return ownSelection;
    }
    const selection: RowSelectionState = {};
    shownRows.forEach((data, index) => {
      if (isRowSelected(data)) {
        selection[rowIdOf(data, index)] = true;
      }
    });
    return selection;
  }, [isRowSelected, ownSelection, rowIdOf, shownRows]);

  const [expanded, setExpanded] = useState<ExpandedState>(
    isExpandedByDefault ? true : {},
  );

  bridge.getSortModel = () => sortModelFromSorting(sortingRef.current);
  bridge.setSortModel = (sortModel) =>
    onSortingChange(sortingFromSortModel(sortModel));
  bridge.isRowSelected = (rowId) => !!rowSelection[rowId];
  // AG's rowSelection.isRowSelectable (or the older isRowSelectable option):
  // a row it turns down can't be selected
  const isRowSelectable:
    | ((node: { data: DataTable.Row }) => boolean)
    | undefined =
    bridge.gridOptions.rowSelection?.isRowSelectable ??
    bridge.gridOptions.isRowSelectable;
  bridge.setRowSelected = ({ rowId, isSelected, clearOthers }) => {
    const data = rowsByIdRef.current.get(rowId);
    if (
      isSelected &&
      isRowSelectable &&
      (!data || !isRowSelectable({ data }))
    ) {
      return;
    }
    setOwnSelection((current) => {
      const isAlready = !!current[rowId] === isSelected;
      const hasOthers = Object.keys(current).some((id) => id !== rowId);
      // Unchanged: keep the same object, so nothing re-renders (Board's drill
      // highlight sets every row's selection after each page)
      if (isAlready && !(clearOthers && hasOthers)) {
        return current;
      }
      const next: RowSelectionState = clearOthers ? {} : { ...current };
      if (isSelected) {
        next[rowId] = true;
      } else {
        delete next[rowId];
      }
      return next;
    });
  };
  bridge.deselectAll = () =>
    setOwnSelection((current) =>
      Object.keys(current).length === 0 ? current : {},
    );
  bridge.getSelectedRows = () =>
    shownRows.filter((data, index) => !!rowSelection[rowIdOf(data, index)]);
  // AG's rowSelected (on each row whose selection changed) and
  // selectionChanged, once the change is drawn: SelectDocumentCell keeps its
  // owner's selected ids in step through them
  const previousSelectionRef = useRef<RowSelectionState>({});
  useEffect(() => {
    const previous = previousSelectionRef.current;
    previousSelectionRef.current = rowSelection;
    const changed = _.uniq([
      ...Object.keys(previous),
      ...Object.keys(rowSelection),
    ]).filter((rowId) => !!previous[rowId] !== !!rowSelection[rowId]);
    if (changed.length === 0) {
      return;
    }
    changed.forEach((rowId) =>
      bridge.events.emit({ rowId, event: 'rowSelected' }),
    );
    bridge.tableEvents.emit('selectionChanged', { source: 'api' });
  }, [bridge, rowSelection]);

  const table = useTable({
    features: DATA_TABLE_FEATURES,
    columns,
    data: shownRows,
    state: { sorting, rowSelection, expanded, columnVisibility },
    onSortingChange,
    onRowSelectionChange: setOwnSelection,
    onExpandedChange: setExpanded,
    manualSorting: !!isManualSorting,
    enableSortingRemoval: true,
    getSubRows,
    getRowId: tableGetRowId,
  });

  // useTable hands back a new wrapper on every render (its options are a new
  // object each time), so anything memoised reads the table through a ref
  const tableRef = useRef(table);
  tableRef.current = table;

  // Nodes of rows that have gone (a new sort, filter or date range) would
  // otherwise hold their data for as long as the table is shown. The totals
  // rows' nodes stay
  useEffect(() => {
    const rowIds = new Set(
      tableRef.current.getCoreRowModel().flatRows.map((row) => row.id),
    );
    nodeCache.current.forEach((node, rowId) => {
      if (!rowIds.has(rowId) && !node.isRowPinned()) {
        nodeCache.current.delete(rowId);
      }
    });
  }, [shownRows]);

  const api = useMemo(() => buildApiShim(bridge), [bridge]);
  // Every leaf column, and the ones shown
  const allLeafColumns = useMemo(
    () => tableRef.current.getAllLeafColumns(),
    // The leaf columns change when the columns do
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );
  const leafColumns = useMemo(
    () => allLeafColumns.filter((column) => !metaOf(column).isHidden),
    [allLeafColumns],
  );
  const columnShims = useMemo(() => {
    const shims = new Map<string, ColumnShim>();
    allLeafColumns.forEach((column) => {
      const meta = metaOf(column);
      shims.set(
        column.id,
        buildColumnShim({
          bridge,
          colId: column.id,
          colDef: meta.colDef,
          userColDef: meta.userColDef,
        }),
      );
    });
    return shims;
  }, [bridge, allLeafColumns]);

  const getNode = useCallback(
    ({
      rowId,
      data,
      rowIndex,
      isPinned,
    }: {
      rowId: string;
      data: DataTable.Row;
      rowIndex: number;
      isPinned: boolean;
    }) => {
      const cached = nodeCache.current.get(rowId);
      if (cached && cached.data === data && cached.rowIndex === rowIndex) {
        return cached;
      }
      const node = buildNodeShim({ bridge, rowId, data, rowIndex, isPinned });
      nodeCache.current.set(rowId, node);
      return node;
    },
    [bridge],
  );
  const getBodyNode = useCallback(
    (tableRow: TableRow) =>
      getNode({
        rowId: tableRow.id,
        data: tableRow.original,
        rowIndex: tableRow.index,
        isPinned: false,
      }),
    [getNode],
  );
  bridge.getColumn = (colId) => columnShims.get(colId) ?? null;
  bridge.forEachNode = (callback) =>
    tableRef.current
      .getCoreRowModel()
      .rows.forEach((tableRow, index) =>
        callback(getBodyNode(tableRow), index),
      );
  bridge.tableEvents.setApi(api);

  // Columns sized by their content, as AG's auto-size: the fitted ones when
  // fitting the width, every column when fitting the content. Like AG, they
  // are measured once (when the columns or data change, not on every render)
  // and then hold that width, so content that comes and goes (a hovered row's
  // link button, the rows a virtualised table scrolls in) can't resize them.
  const fittedIdsKey = (fittedColumnIds ?? []).join(',');
  // Keyed by the set of columns, not their order, so moving columns doesn't
  // measure them again
  const contentSizedKey = useMemo(() => {
    if (sizing === 'fitContent') {
      return _.sortBy(leafColumns.map((c) => c.id)).join(',');
    }
    const ids = fittedIdsKey ? fittedIdsKey.split(',') : [];
    if (isFirstColumnFitted && leafColumns[0]) {
      ids.push(leafColumns[0].id);
    }
    return _.sortBy(
      leafColumns.map((c) => c.id).filter((id) => ids.includes(id)),
    ).join(',');
  }, [fittedIdsKey, isFirstColumnFitted, leafColumns, sizing]);
  const contentSizedIds = useMemo(
    () => (contentSizedKey ? contentSizedKey.split(',') : []),
    [contentSizedKey],
  );
  // A virtualised table measures its first page, as AG sizes on first render
  const measuredRows = isVirtualised ? rows.length > 0 : rows;
  // Widths the user dragged columns to (isResizable). Not saved: they last
  // as long as the table is shown, as AG's do
  const [userWidths, setUserWidths] = useState<{
    [columnId: string]: number;
  }>({});
  // A drag ends with a click on the header, which mustn't sort it
  const isResizingRef = useRef<boolean>(false);
  const startResize = useCallback(
    ({
      event,
      columnId,
      minWidth,
    }: {
      event: React.PointerEvent<HTMLElement>;
      columnId: string;
      minWidth: number | undefined;
    }) => {
      const header = headerCellRefs.current.get(columnId);
      if (!header) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      isResizingRef.current = true;
      const startX = event.clientX;
      const startWidth = header.offsetWidth;
      const narrowest = Math.max(MIN_RESIZED_COLUMN_WIDTH, minWidth ?? 0);
      const onMove = (moveEvent: PointerEvent) => {
        const width = Math.max(
          narrowest,
          Math.round(startWidth + moveEvent.clientX - startX),
        );
        setUserWidths((current) =>
          current[columnId] === width
            ? current
            : { ...current, [columnId]: width },
        );
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        // After the click this release fires
        setTimeout(() => {
          isResizingRef.current = false;
        });
        bridgeRef.current.tableEvents.emit('columnResized', {
          finished: true,
          source: 'uiColumnResized',
        });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [],
  );

  // AG's column state: every column in the order shown, with its width,
  // whether it's hidden, and its sort
  bridge.getColumnState = () =>
    allLeafColumns.map((column): ShimColumnState => {
      const sortIndex = sortingRef.current.findIndex((s) => s.id === column.id);
      const sort = sortingRef.current[sortIndex];
      return {
        colId: column.id,
        width:
          userWidths[column.id] ??
          (headerCellRefs.current.get(column.id)?.offsetWidth ||
            metaOf(column).width),
        hide: !!metaOf(column).isHidden,
        sort: sort ? (sort.desc ? 'desc' : 'asc') : null,
        sortIndex: sort ? sortIndex : null,
      };
    });
  bridge.getColumns = () =>
    allLeafColumns.flatMap((column) => {
      const shim = columnShims.get(column.id);
      return shim ? [shim] : [];
    });
  bridge.isColumnVisible = (colId) =>
    allLeafColumns.some(
      (column) => column.id === colId && !metaOf(column).isHidden,
    );
  // Moves, shows or hides columns (the state's order, and its `hide`), and
  // sets their widths, as AG's applyColumnState. Tells column listeners of
  // whatever changed, once it's drawn
  bridge.applyColumnLayout = ({ state, applyOrder, defaultHide }) => {
    const current = columnLayoutRef.current;
    const hidden = { ...current.hidden };
    if (defaultHide !== undefined) {
      allLeafColumns.forEach((column) => {
        hidden[column.id] = defaultHide;
      });
    }
    state.forEach((s) => {
      if (s.hide !== undefined && s.hide !== null) {
        hidden[s.colId] = s.hide;
      }
    });
    const order = applyOrder ? state.map((s) => s.colId) : current.order;
    if (
      !_.isEqual(hidden, current.hidden) ||
      !_.isEqual(order, current.order)
    ) {
      changeColumnLayout({ order, hidden });
    }
    const isVisibilityChanged = allLeafColumns.some(
      (column) =>
        hidden[column.id] !== undefined &&
        hidden[column.id] !== !!metaOf(column).isHidden,
    );
    if (isVisibilityChanged) {
      pendingColumnEventsRef.current.add('columnVisible');
    }
    const shownIds = allLeafColumns.map((column) => column.id);
    if (
      applyOrder &&
      !_.isEqual(
        _.sortBy(shownIds, (id) => {
          const position = state.findIndex((s) => s.colId === id);
          return position === -1 ? state.length : position;
        }),
        shownIds,
      )
    ) {
      pendingColumnEventsRef.current.add('columnMoved');
    }
    const widths = state.filter(
      (s): s is ShimColumnState & { width: number } =>
        typeof s.width === 'number' && s.width > 0,
    );
    if (widths.some((s) => userWidths[s.colId] !== s.width)) {
      setUserWidths((currentWidths) => {
        const next = { ...currentWidths };
        widths.forEach((s) => {
          next[s.colId] = Math.max(MIN_RESIZED_COLUMN_WIDTH, s.width);
        });
        return next;
      });
      pendingColumnEventsRef.current.add('columnResized');
    }
  };

  // A header dragged sideways moves its column, or a group header its
  // group's columns, among its siblings pinned the same way (so a group's
  // columns stay together); dragged off the table, above or below, it hides
  // them, as AG's movable columns. As in AG, the columns move (sliding
  // aside) while it's dragged, and a ghost of it follows the pointer; letting
  // go keeps the order, which is when the owner hears of it
  const startColumnDrag = ({
    event,
    column,
  }: {
    event: React.PointerEvent<HTMLElement>;
    column: TableColumn;
  }) => {
    if (event.button !== 0) {
      return;
    }
    const startX = event.clientX;
    const startY = event.clientY;
    const shownLeavesOf = (c: TableColumn) =>
      c.getLeafColumns().filter((leaf) => !metaOf(leaf).isHidden);
    // A group's pin, when its columns share one
    const pinOf = (c: TableColumn) => {
      const pins = _.uniq(shownLeavesOf(c).map((leaf) => metaOf(leaf).pinned));
      return pins.length === 1 ? pins[0] : 'mixed';
    };
    const pin = pinOf(column);
    const movedIds = column.getLeafColumns().map((leaf) => leaf.id);
    // By id: the columns are rebuilt each time the preview redraws them
    const siblings = (
      column.parent ? column.parent.columns : tableRef.current.getAllColumns()
    )
      .filter(
        (c) =>
          c.id !== column.id && shownLeavesOf(c).length > 0 && pinOf(c) === pin,
      )
      .map((c) => ({
        id: c.id,
        isLeaf: isLeafColumn(c),
        leafIds: c.getLeafColumns().map((leaf) => leaf.id),
      }));
    const rectOf = ({ id, isLeaf }: { id: string; isLeaf: boolean }) =>
      (isLeaf ? headerCellRefs : groupHeaderRefs).current
        .get(id)
        ?.getBoundingClientRect();
    const own = { id: column.id, isLeaf: isLeafColumn(column) };
    const label = `${column.columnDef.header ?? ''}`;
    const committedIds = allLeafColumns.map((leaf) => leaf.id);
    let order = committedIds;
    let isOutside = false;
    let isDragging = false;
    let lastX = startX;

    const show = (next: { order: string[]; isOutside: boolean }) => {
      if (
        isDragging &&
        next.isOutside === isOutside &&
        _.isEqual(next.order, order)
      ) {
        return;
      }
      if (!_.isEqual(next.order, order)) {
        snapshotColumns();
      }
      isDragging = true;
      order = next.order;
      isOutside = next.isOutside;
      setColumnDrag({
        columnId: column.id,
        label,
        isOutside,
        previewOrder: _.isEqual(order, committedIds) ? undefined : order,
        start: { x: startX, y: startY },
      });
    };

    const onMove = (moveEvent: PointerEvent) => {
      if (
        !isDragging &&
        Math.abs(moveEvent.clientX - startX) < COLUMN_DRAG_THRESHOLD &&
        Math.abs(moveEvent.clientY - startY) < COLUMN_DRAG_THRESHOLD
      ) {
        return;
      }
      // The release ends with a click on the header, which mustn't sort it
      isResizingRef.current = true;
      const x = moveEvent.clientX;
      const dx = x - lastX;
      lastX = x;
      const scrollerRect = scrollerRef.current?.getBoundingClientRect();
      if (
        scrollerRect &&
        (moveEvent.clientY < scrollerRect.top - HIDE_DRAG_DISTANCE ||
          moveEvent.clientY > scrollerRect.bottom + HIDE_DRAG_DISTANCE)
      ) {
        show({ order: committedIds, isOutside: true });
        return;
      }
      const isOver = (rect: DOMRect | undefined) =>
        !!rect && x >= rect.left && x < rect.right;
      const rects = siblings.flatMap((sibling) => {
        const rect = rectOf(sibling);
        return rect ? [{ sibling, rect }] : [];
      });
      // Still over its own header: it stays where it is. Past the first or
      // last header: to that end
      const first = _.minBy(rects, ({ rect }) => rect.left);
      const last = _.maxBy(rects, ({ rect }) => rect.right);
      const over = isOver(rectOf(own))
        ? undefined
        : rects.find(({ rect }) => isOver(rect)) ??
          (first && x < first.rect.left
            ? first
            : last && x >= last.rect.right
              ? last
              : undefined);
      let nextOrder = order;
      if (over) {
        // Only in the direction it's moving, so a column swapped with a
        // narrower one doesn't swap straight back under the pointer
        const isAfter =
          order.indexOf(over.sibling.leafIds[0]) > order.indexOf(movedIds[0]);
        if (isAfter ? dx >= 0 : dx <= 0) {
          nextOrder = moveColumnBlock({
            ids: order,
            movedIds,
            targetIds: over.sibling.leafIds,
            side: isAfter ? 'after' : 'before',
          });
        }
      }
      show({ order: nextOrder, isOutside: false });
    };
    const finish = ({ isCancelled }: { isCancelled: boolean }) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      setTimeout(() => {
        isResizingRef.current = false;
      });
      if (!isDragging) {
        return;
      }
      if (isCancelled || isOutside) {
        if (!_.isEqual(order, committedIds)) {
          snapshotColumns();
        }
        setColumnDrag(undefined);
      }
      if (isCancelled) {
        return;
      }
      const current = columnLayoutRef.current;
      if (isOutside) {
        changeColumnLayout({
          ...current,
          hidden: {
            ...current.hidden,
            ...Object.fromEntries(movedIds.map((id) => [id, true])),
          },
        });
        pendingColumnEventsRef.current.add('columnVisible');
        return;
      }
      // Kept where the preview drew it, so nothing moves on letting go
      if (!_.isEqual(order, committedIds)) {
        changeColumnLayout({ ...current, order });
        pendingColumnEventsRef.current.add('columnMoved');
      }
      setColumnDrag(undefined);
    };
    const onUp = () => finish({ isCancelled: false });
    const onCancel = () => finish({ isCancelled: true });
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  const [lockedWidths, setLockedWidths] = useState<
    { [columnId: string]: number } | undefined
  >();
  useLayoutEffect(() => {
    setLockedWidths(undefined);
  }, [contentSizedIds, measuredRows, totalsRows]);
  // Text measured before its web font arrives is narrower: measure again
  // whenever a font finishes loading. A weight loads only once something
  // uses it, so the header's can arrive after the table has mounted, even
  // when the page's other fonts had already loaded
  useEffect(() => {
    const { fonts } = document;
    if (!fonts) {
      return;
    }
    const remeasure = () => setLockedWidths(undefined);
    fonts.addEventListener('loadingdone', remeasure);
    return () => fonts.removeEventListener('loadingdone', remeasure);
  }, []);
  // Measuring lays the table out at its content's natural width (no flex
  // shares, no filler), so each content-sized column takes the full width of
  // its widest cell, as AG's auto-size measures it. It happens in layout
  // effects, so it's never painted. jsdom has no layout to measure.
  const isMeasuring =
    process.env.NODE_ENV !== 'test' &&
    lockedWidths === undefined &&
    contentSizedIds.length > 0;
  const [measureAttempt, setMeasureAttempt] = useState<number>(0);
  useLayoutEffect(() => {
    if (!isMeasuring) {
      return;
    }
    const widths: { [columnId: string]: number } = {};
    contentSizedIds.forEach((id) => {
      widths[id] = headerCellRefs.current.get(id)?.offsetWidth ?? 0;
    });
    // A virtualised table draws no rows until its virtualiser has measured
    // the scroller, so the first pass would size the columns by their headers
    // alone, and they'd keep that width. Rows that never draw are given up
    // on, so the table is still sized.
    const isAwaitingRows =
      tableRef.current.getRowModel().rows.length > 0 &&
      !tableElementRef.current?.querySelector('tbody tr[data-row-id]') &&
      measureAttempt < MAX_MEASURE_ATTEMPTS;
    // Not laid out yet (e.g. mounted hidden): try again shortly
    if (isAwaitingRows || Object.values(widths).some((w) => w === 0)) {
      if (measureAttempt < MAX_MEASURE_ATTEMPTS) {
        const frame = requestAnimationFrame(() =>
          setMeasureAttempt((attempt) => attempt + 1),
        );
        return () => cancelAnimationFrame(frame);
      }
      return;
    }
    setMeasureAttempt(0);
    // AG's autoSizePadding: the room a hovered row's link button shows in
    Object.keys(widths).forEach((id) => {
      widths[id] += AUTO_SIZE_PADDING;
    });
    setLockedWidths(widths);
  }, [contentSizedIds, isMeasuring, measureAttempt]);

  // Sticky offsets for pinned columns, from their measured header widths
  const [pinnedOffsets, setPinnedOffsets] = useState<{
    [columnId: string]: { left?: number; right?: number };
  }>({});
  useLayoutEffect(() => {
    const next: { [columnId: string]: { left?: number; right?: number } } = {};
    let left = 0;
    leafColumns
      .filter((c) => metaOf(c).pinned === 'left')
      .forEach((c) => {
        next[c.id] = { left };
        left += headerCellRefs.current.get(c.id)?.offsetWidth ?? 0;
      });
    let right = 0;
    [...leafColumns]
      .reverse()
      .filter((c) => metaOf(c).pinned === 'right')
      .forEach((c) => {
        next[c.id] = { right };
        right += headerCellRefs.current.get(c.id)?.offsetWidth ?? 0;
      });
    setPinnedOffsets((current) =>
      JSON.stringify(current) === JSON.stringify(next) ? current : next,
    );
  }, [leafColumns, rows, totalsRows, sizing, lockedWidths, userWidths]);

  // fitWidth: the columns sized by their content, the others sharing what
  // they leave
  const fitted = useMemo(() => {
    const ids = new Set(fittedIdsKey ? fittedIdsKey.split(',') : []);
    if (isFirstColumnFitted && leafColumns[0]) {
      ids.add(leafColumns[0].id);
    }
    return ids;
  }, [fittedIdsKey, isFirstColumnFitted, leafColumns]);

  const isTableShown =
    !(isLoading && rows.length === 0) && !(noDataToDisplay && !isLoading);

  // fitWidth with column limits (AG's sizeColumnsToFit with min and max
  // widths). A table cell ignores max-width, so the shared widths are worked
  // out in pixels from the container's width, and again as it resizes. Any
  // width the limits leave over goes to the filler column.
  const hasWidthLimits =
    sizing === 'fitWidth' &&
    leafColumns.some((c) => metaOf(c).maxWidth !== undefined);
  const containerWidth = useContainerWidth({
    ref: scrollerRef,
    isEnabled: hasWidthLimits && isTableShown,
    isPaused: !!isRefitPaused,
  });
  const limitedLayout = useMemo(() => {
    if (!hasWidthLimits || containerWidth <= 0 || isMeasuring) {
      return undefined;
    }
    const isShared = (c: TableColumn) =>
      !fitted.has(c.id) && !userWidths[c.id] && (metaOf(c).flex ?? 0) > 0;
    const widthOf = (c: TableColumn) =>
      userWidths[c.id] ?? lockedWidths?.[c.id] ?? metaOf(c).width ?? 0;
    const takenWidth = leafColumns
      .filter((c) => !isShared(c))
      .reduce((sum, c) => sum + widthOf(c), 0);
    const widths = fitColumnsToWidth({
      columns: leafColumns.filter(isShared).map((c) => {
        const meta = metaOf(c);
        return {
          id: c.id,
          flex: meta.flex ?? 0,
          minWidth: meta.minWidth,
          maxWidth: meta.maxWidth,
        };
      }),
      width: containerWidth - takenWidth,
    });
    const sharedWidth = Object.values(widths).reduce((sum, w) => sum + w, 0);
    return { widths, tableWidth: takenWidth + sharedWidth };
  }, [
    containerWidth,
    fitted,
    hasWidthLimits,
    isMeasuring,
    leafColumns,
    lockedWidths,
    userWidths,
  ]);

  const columnStyles = useMemo(() => {
    const styles = new Map<string, CSSProperties>();
    const totalFlex = leafColumns
      .filter((c) => !fitted.has(c.id))
      .reduce((sum, c) => sum + (metaOf(c).flex ?? 0), 0);
    leafColumns.forEach((column) => {
      const meta = metaOf(column);
      const style: CSSProperties = {};
      const lockedWidth = lockedWidths?.[column.id];
      const userWidth = userWidths[column.id];
      if (userWidth) {
        // Dragged to this width: it holds whatever the sizing would give
        style.width = userWidth;
        style.minWidth = userWidth;
        style.maxWidth = userWidth;
        style.overflow = 'hidden';
      } else if (isMeasuring) {
        if (contentSizedIds.includes(column.id)) {
          style.whiteSpace = 'nowrap';
        }
      } else if (lockedWidth) {
        // Sized by its content once; what shows on hover (an entity's link
        // button) mustn't widen it, as in AG
        style.width = lockedWidth;
        style.minWidth = lockedWidth;
        style.maxWidth = lockedWidth;
        style.overflow = 'hidden';
      } else if (sizing === 'fixed') {
        const width = meta.width ?? DEFAULT_FIXED_COLUMN_WIDTH;
        style.width = width;
        style.minWidth = width;
        style.maxWidth = width;
        style.overflow = 'hidden';
      } else if (sizing === 'fitWidth') {
        const limitedWidth = limitedLayout?.widths[column.id];
        if (limitedWidth !== undefined) {
          style.width = limitedWidth;
          style.minWidth = limitedWidth;
          style.maxWidth = limitedWidth;
          style.overflow = 'hidden';
        } else if (fitted.has(column.id)) {
          // As AG's autoSizeColumns, which measures the header too
          style.width = '1%';
          style.whiteSpace = 'nowrap';
        } else if (meta.flex && totalFlex > 0) {
          style.width = `${(100 * meta.flex) / totalFlex}%`;
          style.maxWidth = 0;
        }
      }
      if (meta.width && !style.width) {
        style.width = meta.width;
      }
      if (meta.minWidth && !lockedWidth && !userWidth && !isMeasuring) {
        style.minWidth = meta.minWidth;
      }
      const offset = pinnedOffsets[column.id];
      if (offset && !isMeasuring) {
        style.left = offset.left;
        style.right = offset.right;
      }
      styles.set(column.id, style);
    });
    return styles;
  }, [
    contentSizedIds,
    fitted,
    isMeasuring,
    leafColumns,
    limitedLayout,
    lockedWidths,
    pinnedOffsets,
    sizing,
    userWidths,
  ]);

  const minTableWidth = useMemo(() => {
    if (sizing !== 'fitWidth') {
      return undefined;
    }
    if (limitedLayout) {
      return limitedLayout.tableWidth;
    }
    const sum = leafColumns.reduce(
      (total, c) => total + (metaOf(c).minWidth ?? 0),
      0,
    );
    return sum > 0 ? sum : undefined;
  }, [leafColumns, limitedLayout, sizing]);

  const tableRows = table.getRowModel().rows;

  // Row virtualisation (server-side tables). Small client-side tables render
  // every row and let the browser lay them out.
  const [theadHeight, setTheadHeight] = useState<number>(0);
  useLayoutEffect(() => {
    const height = theadRef.current?.offsetHeight ?? 0;
    setTheadHeight((current) => (current === height ? current : height));
  }, [leafColumns, rows, sizing]);
  const virtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: tableRows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => rowHeight + 1,
    overscan: OVERSCAN,
    enabled: !!isVirtualised,
    scrollMargin: theadHeight,
    // Re-render only when the rows in view change (or scrolling starts or
    // stops), not on every resize or scroll frame. The direct DOM writes this
    // also enables need a containerRef or measured rows, which the table
    // doesn't use (it pads with spacer rows), so they never happen.
    directDomUpdates: true,
    ...(process.env.NODE_ENV === 'test'
      ? {
          initialRect: TEST_RECT,
          observeElementRect: (
            _instance: unknown,
            cb: (rect: { width: number; height: number }) => void,
          ) => cb(TEST_RECT),
        }
      : {}),
  });
  const virtualItems = isVirtualised ? virtualizer.getVirtualItems() : [];

  // Keyboard navigation (one tab stop, arrow keys between cells)
  const leafIndexes = useMemo(
    () => new Map(leafColumns.map((column, index) => [column.id, index])),
    [leafColumns],
  );
  const nav = useGridNavigation({
    tableRef: tableElementRef,
    colCount: leafColumns.length,
    bodyRowCount: tableRows.length,
    footerRowCount: totalsRows.length,
    scrollToBodyRow: isVirtualised
      ? (bodyIndex) => virtualizer.scrollToIndex(bodyIndex)
      : undefined,
  });
  // A selected row (a matrix's drill highlight) may not be drawn when the
  // table is virtualised: bring it into view, as AG's ensureIndexVisible
  useEffect(() => {
    if (!isVirtualised) {
      return;
    }
    const index = tableRef.current
      .getRowModel()
      .rows.findIndex((row) => row.getIsSelected());
    if (index >= 0) {
      virtualizer.scrollToIndex(index, { align: 'auto' });
    }
    // Only when the selection changes, not as the user scrolls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVirtualised, rowSelection]);

  const lastVirtualIndex =
    virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1;

  const onEndReachedRef = useRef(onEndReached);
  onEndReachedRef.current = onEndReached;
  useEffect(() => {
    // Only a table that scrolls asks for more. One whose container grows with
    // it (a parent without a bounded height) shows every row, so the end
    // always looks reached: each page would ask for the next, until the
    // whole dataset was loaded
    const scroller = scrollerRef.current;
    const isScrollable =
      !!scroller && scroller.scrollHeight > scroller.clientHeight;
    if (
      isVirtualised &&
      isScrollable &&
      lastVirtualIndex >= 0 &&
      lastVirtualIndex >= tableRows.length - END_REACHED_THRESHOLD &&
      onEndReachedRef.current
    ) {
      onEndReachedRef.current();
    }
  }, [isVirtualised, lastVirtualIndex, tableRows.length]);

  // Export: the rows as shown (sorted, all of them, not just the rendered
  // window), processed the way AG's export callbacks process them. Read
  // when exporting, as AG's api reads its grid then: the owner gets a new
  // handle after each change, but a handle it hasn't swapped in yet still
  // exports what's drawn.
  const exportedRef = useRef({
    columns,
    totalsRows,
    rowSelection,
    isParentRowExported,
  });
  exportedRef.current = {
    columns,
    totalsRows,
    rowSelection,
    isParentRowExported,
  };
  const exportHandle = useMemo(
    (): DataTable.ExportHandle => ({
      getCells: ({
        processCell,
        processHeader,
        isTotalsRowIncluded,
        onlySelected,
        columnIds,
      }) =>
        buildExportCells({
          columns: exportedRef.current.columns,
          rows: tableRef.current
            .getSortedRowModel()
            // Depth first (each parent, then its sub-rows), as sorted
            .flatRows.filter(
              (r) =>
                (r.subRows.length === 0 ||
                  !!exportedRef.current.isParentRowExported) &&
                (!onlySelected || !!exportedRef.current.rowSelection[r.id]),
            )
            .map((r) => r.original),
          totals: isTotalsRowIncluded
            ? exportedRef.current.totalsRows
            : EMPTY_ROWS,
          processCell,
          processHeader,
          columnIds,
        }),
      loadedRowCount: shownRows.length,
      totalRowCount,
    }),
    // sorting is read through the table when exporting, but a new handle
    // tells the owner something changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, shownRows, sorting, totalRowCount, totalsRows],
  );
  const onExportHandleRef = useRef(onExportHandle);
  onExportHandleRef.current = onExportHandle;
  useEffect(() => {
    if (onExportHandleRef.current) {
      onExportHandleRef.current(exportHandle);
    }
  }, [exportHandle]);
  useEffect(
    () => () => {
      if (onExportHandleRef.current) {
        onExportHandleRef.current(undefined);
      }
    },
    [],
  );

  const isEmpty = !!noDataToDisplay || rows.length === 0;
  useHeadlessHeight({
    isEnabled: !!isHeadlessHeightEnabled && !!isPdfExport,
    tableRef: tableElementRef,
    isEmpty: isEmpty || !!isLoading,
  });

  // Totals sit at the bottom of the table's space, as AG pins them, except
  // where the table is as tall as its rows (natural layout, the PDF)
  const bodyFillHeight = useBodyFillHeight({
    isEnabled:
      isTableShown && totalsRows.length > 0 && scrollerLayout === 'fill',
    scrollerRef,
    tableRef: tableElementRef,
  });

  // Once per id, after the widths are locked (so the scroll lands where the
  // column ends up), bring the column into view beside the pinned ones. Not
  // on every render, which would undo the user's own scrolling.
  const scrolledToColumnIdRef = useRef<string | undefined>();
  useLayoutEffect(() => {
    if (
      !scrollToColumnId ||
      isMeasuring ||
      !isTableShown ||
      scrolledToColumnIdRef.current === scrollToColumnId
    ) {
      return;
    }
    const scroller = scrollerRef.current;
    const header = headerCellRefs.current.get(scrollToColumnId);
    if (!scroller || !header) {
      return;
    }
    const pinnedWidth = (side: 'left' | 'right') =>
      leafColumns
        .filter((c) => metaOf(c).pinned === side)
        .reduce(
          (sum, c) =>
            sum + (headerCellRefs.current.get(c.id)?.offsetWidth ?? 0),
          0,
        );
    scrolledToColumnIdRef.current = scrollToColumnId;
    const start = header.offsetLeft - pinnedWidth('left');
    const end =
      header.offsetLeft +
      header.offsetWidth -
      scroller.clientWidth +
      pinnedWidth('right');
    if (start < scroller.scrollLeft) {
      scroller.scrollLeft = start;
    } else if (end > scroller.scrollLeft) {
      scroller.scrollLeft = end;
    }
  }, [isMeasuring, isTableShown, leafColumns, scrollToColumnId]);

  // The api for owners written against AG's GridApi (onGridReady), and
  // whether the table has gone (AG's api.isDestroyed)
  const onGridReadyRef = useRef(onGridReady);
  onGridReadyRef.current = onGridReady;
  useEffect(() => {
    bridge.isDestroyed = false;
    if (onGridReadyRef.current) {
      onGridReadyRef.current({ api: api as GridApi });
    }
    return () => {
      bridge.isDestroyed = true;
    };
  }, [api, bridge]);

  // AG's grid events: the rows changed (a page, a sort, an edit), and the
  // first rows are drawn (once)
  useEffect(() => {
    bridge.tableEvents.emit('modelUpdated');
  }, [bridge, shownRows, sorting]);
  const hasRows = shownRows.length > 0;
  const hasRenderedRowsRef = useRef<boolean>(false);
  useEffect(() => {
    if (hasRows && !hasRenderedRowsRef.current) {
      hasRenderedRowsRef.current = true;
      bridge.tableEvents.emit('firstDataRendered');
    }
  }, [bridge, hasRows]);

  // AG's ensureNodeVisible(node, 'middle'): scroll the table (not the page)
  // so the row sits in the middle
  bridge.scrollToRow = (rowId) => {
    const index = tableRef.current
      .getRowModel()
      .rows.findIndex((tableRow) => tableRow.id === rowId);
    if (index < 0) {
      return;
    }
    if (isVirtualised) {
      virtualizer.scrollToIndex(index, { align: 'center' });
      return;
    }
    const scroller = scrollerRef.current;
    const rowElement = Array.from(
      tableElementRef.current?.querySelectorAll<HTMLElement>(
        'tr[data-row-id]',
      ) ?? [],
    ).find((element) => element.getAttribute('data-row-id') === rowId);
    if (!scroller || !rowElement) {
      return;
    }
    const rowRect = rowElement.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    scroller.scrollTop +=
      rowRect.top -
      scrollerRect.top -
      (scroller.clientHeight - rowRect.height) / 2;
  };

  const leafById = useMemo(
    () => new Map(allLeafColumns.map((column) => [column.id, column])),
    [allLeafColumns],
  );

  // The row, column and value a body or totals cell shows, as AG hands them
  // to its menu and editors
  const getCellTarget = (cell: HTMLElement) => {
    const colId = cell.getAttribute('data-col-id');
    const rowElement = cell.closest('tr');
    const leaf = colId ? leafById.get(colId) : undefined;
    if (!colId || !rowElement || !leaf) {
      return undefined;
    }
    const column = columnShims.get(colId) ?? null;
    const pinnedIndex = rowElement.getAttribute('data-pinned-index');
    if (pinnedIndex !== null) {
      const index = Number(pinnedIndex);
      const data = totalsRows[index];
      if (!data) {
        return undefined;
      }
      return {
        node: getNode({
          rowId: `pinned-${index}`,
          data,
          rowIndex: index,
          isPinned: true,
        }),
        column,
        value: leaf.accessorFn ? leaf.accessorFn(data, index) : undefined,
        tableRow: undefined,
      };
    }
    const rowId = rowElement.getAttribute('data-row-id');
    const tableRow = rowId
      ? tableRef.current.getCoreRowModel().rowsById[rowId]
      : undefined;
    if (!tableRow) {
      return undefined;
    }
    return {
      node: getBodyNode(tableRow),
      column,
      value: tableRow.getValue(colId),
      tableRow,
    };
  };

  // Cell editing (AG's popup cell editors with singleClickEdit). The editor
  // reports its value (onValueChange) and stopEditing writes it to the row;
  // cancelling drops it.
  const [editingCell, setEditingCell] = useState<EditingCell | undefined>();
  const editingCellRef = useRef<EditingCell | undefined>(editingCell);
  const editorValueRef = useRef<{ value: unknown } | undefined>();
  const stopEditing = useCallback(
    (isCancelled?: boolean) => {
      const editing = editingCellRef.current;
      if (!editing) {
        return;
      }
      const edited = editorValueRef.current;
      editingCellRef.current = undefined;
      editorValueRef.current = undefined;
      setEditingCell(undefined);
      if (isCancelled) {
        return;
      }
      if (edited) {
        const field = getColumnField({ bridge, colId: editing.colId });
        bridge.updateRowData({
          rowId: editing.rowId,
          change: (data) => ({ ...data, [field]: edited.value }),
        });
      }
      // Back to the cell, as AG returns focus once an edit is done
      if (editing.anchor.isConnected) {
        editing.anchor.focus({ preventScroll: true });
      }
    },
    [bridge],
  );
  bridge.stopEditing = stopEditing;
  const hasEditors = allLeafColumns.some((column) => !!metaOf(column).editor);
  const startEditing = (cell: HTMLElement) => {
    const target = getCellTarget(cell);
    const colId = cell.getAttribute('data-col-id');
    const leaf = colId ? leafById.get(colId) : undefined;
    const editor = leaf ? metaOf(leaf).editor : undefined;
    if (
      !colId ||
      !target ||
      !target.tableRow ||
      !editor ||
      !editor.isEditable({ data: target.tableRow.original, node: target.node })
    ) {
      return false;
    }
    editorValueRef.current = undefined;
    const editing = { rowId: target.tableRow.id, colId, anchor: cell };
    editingCellRef.current = editing;
    setEditingCell(editing);
    return true;
  };
  const getCell = (target: EventTarget | null) =>
    target instanceof Element
      ? target.closest<HTMLElement>('td[data-col-id]')
      : null;
  // AG's cellClicked (a body or totals cell), then the editor, if the cell
  // has one
  const onTableClick = (event: React.MouseEvent) => {
    const cell = getCell(event.target);
    if (!cell) {
      return;
    }
    const target = getCellTarget(cell);
    if (target) {
      emitTableEvent('cellClicked', {
        colDef: target.column?.getColDef(),
        column: target.column,
        value: target.value,
        data: target.node.data,
        node: target.node,
        rowIndex: target.node.rowIndex,
        rowPinned: target.node.rowPinned,
        event: event.nativeEvent,
      });
    }
    if (hasEditors) {
      startEditing(cell);
    }
  };
  const onTableKeyDown = (event: React.KeyboardEvent) => {
    if (
      hasEditors &&
      (event.key === 'Enter' || event.key === 'F2') &&
      event.target instanceof HTMLElement &&
      event.target.matches('td[data-col-id]') &&
      startEditing(event.target)
    ) {
      event.preventDefault();
      return;
    }
    nav.onKeyDown(event);
  };

  // The right-click menu (AG's context menu)
  const [menu, setMenu] = useState<
    { x: number; y: number; entries: MenuEntry[] } | undefined
  >();
  const closeMenu = useCallback(() => setMenu(undefined), []);
  const getContextMenuItemsRef = useRef(getContextMenuItems);
  getContextMenuItemsRef.current = getContextMenuItems;
  const formatCellValue = ({
    leaf,
    value,
    data,
  }: {
    leaf: TableColumn | undefined;
    value: unknown;
    data: DataTable.Row;
  }) => {
    const formatter = leaf ? metaOf(leaf).valueFormatter : undefined;
    return formatter ? formatter(value, data) : value;
  };
  // A cell as AG exports it without a processCellCallback: through its
  // column's value formatter
  const formatExportCell: DataTable.ProcessExportCell = ({
    value,
    column,
    node,
  }) =>
    formatCellValue({
      leaf: leafById.get(column.getColId()),
      value,
      data: node?.data ?? {},
    });
  // What the menu's Export writes: every row, totals too, as AG's default
  // export does
  const getMenuExportCells = () =>
    exportHandle.getCells({
      processCell: formatExportCell,
      isTotalsRowIncluded: true,
    });
  // AG's exportDataAsExcel / exportDataAsCsv / getDataAsCsv, for owners
  // holding the api (the Driver Pins export, DimensionExplorer's CSV)
  const getExportCellsFor = ({
    processCellCallback,
    skipPinnedBottom,
    onlySelected,
    columnKeys,
  }: ShimExportParams) =>
    exportHandle.getCells({
      processCell: processCellCallback ?? formatExportCell,
      isTotalsRowIncluded: !skipPinnedBottom,
      onlySelected,
      columnIds: columnKeys?.map((key) =>
        typeof key === 'string' ? key : key.getColId(),
      ),
    });
  bridge.exportDataAsExcel = (params) => {
    exportTableExcel({
      cells: getExportCellsFor(params),
      fileName: withoutExtension({
        fileName: params.fileName ?? MENU_EXPORT_FILE_NAME,
        extension: 'xlsx',
      }),
      sheetName: params.sheetName,
    });
  };
  bridge.exportDataAsCsv = (params) =>
    downloadTableCsv({
      cells: getExportCellsFor(params),
      fileName: withoutExtension({
        fileName: params.fileName ?? MENU_EXPORT_FILE_NAME,
        extension: 'csv',
      }),
    });
  bridge.getDataAsCsv = (params) => toCsv(getExportCellsFor(params));
  const openContextMenu = ({
    node,
    column,
    value,
    x,
    y,
  }: ContextMenuRequest) => {
    const leaf = column ? leafById.get(column.getColId()) : undefined;
    const cellText = toCellText(
      formatCellValue({ leaf, value, data: node.data }),
    );
    const headerText = leaf ? toCellText(leaf.columnDef.header) : '';
    // AG's Copy with Group Headers: a line per group header row above the
    // column's header, with its groups' labels (blank where it has no group
    // at that level)
    const groupHeaderTexts = (() => {
      const labels: string[] = [];
      let group = leaf?.parent;
      while (group) {
        labels.unshift(toCellText(group.columnDef.header));
        group = group.parent;
      }
      const levels = tableRef.current.getHeaderGroups().length - 1;
      return [
        ...Array<string>(Math.max(0, levels - labels.length)).fill(''),
        ...labels,
      ];
    })();
    const builtIns: { [name: string]: MenuEntry } = {
      copy: { label: 'Copy', onClick: () => writeToClipboard(cellText) },
      copyWithHeaders: {
        label: 'Copy with Headers',
        onClick: () => writeToClipboard(`${headerText}\r\n${cellText}`),
      },
      copyWithGroupHeaders: {
        label: 'Copy with Group Headers',
        onClick: () =>
          writeToClipboard(
            [...groupHeaderTexts, headerText, cellText].join('\r\n'),
          ),
      },
      export: {
        label: 'Export',
        subMenu: [
          {
            label: 'CSV Export',
            onClick: () =>
              downloadTableCsv({
                cells: getMenuExportCells(),
                fileName: MENU_EXPORT_FILE_NAME,
              }),
          },
          {
            label: 'Excel Export',
            onClick: () => {
              exportTableExcel({
                cells: getMenuExportCells(),
                fileName: MENU_EXPORT_FILE_NAME,
              });
            },
          },
        ],
      },
      separator: 'separator',
    };
    const resolve = (items: DataTable.ContextMenuItem[]): MenuEntry[] =>
      items.flatMap((item): MenuEntry[] => {
        if (typeof item === 'string') {
          // AG items we don't draw (Cut, Paste...) are left out
          const builtIn = builtIns[item];
          return builtIn ? [builtIn] : [];
        }
        return [
          {
            label: item.name,
            onClick: item.action,
            isDisabled: item.disabled,
            subMenu: item.subMenu ? resolve(item.subMenu) : undefined,
          },
        ];
      });
    const items = getContextMenuItemsRef.current
      ? getContextMenuItemsRef.current({
          node,
          column,
          value,
          api,
          context: bridge.context,
          defaultItems: DEFAULT_CONTEXT_MENU_ITEMS,
        })
      : DEFAULT_CONTEXT_MENU_ITEMS;
    const entries = resolve(items);
    if (entries.length > 0) {
      setMenu({ x, y, entries });
    }
  };
  bridge.showContextMenu = openContextMenu;
  const onTableContextMenu = (event: React.MouseEvent) => {
    // Ctrl/Cmd + right-click leaves the browser's own menu, as AG does
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    const cell = getCell(event.target);
    const target = cell ? getCellTarget(cell) : undefined;
    if (!cell || !target) {
      return;
    }
    event.preventDefault();
    // From the keyboard (Shift+F10, the menu key) there's no pointer: open
    // it at the focused cell
    const isFromKeyboard = event.clientX === 0 && event.clientY === 0;
    const rect = cell.getBoundingClientRect();
    openContextMenu({
      node: target.node,
      column: target.column,
      value: target.value,
      x: isFromKeyboard ? rect.left : event.clientX,
      y: isFromKeyboard ? rect.bottom : event.clientY,
    });
  };

  // The grid's rowClassRules (AG's), each a function of the row
  const rowClassRules: { [className: string]: unknown } | undefined =
    bridge.gridOptions.rowClassRules;
  const getRowClassName = (tableRow: TableRow) => {
    if (!rowClassRules) {
      return undefined;
    }
    const params = {
      data: tableRow.original,
      node: getBodyNode(tableRow),
      rowIndex: tableRow.index,
      api,
      context: bridge.context,
    };
    const classNames = Object.entries(rowClassRules)
      .filter(([, rule]) => typeof rule === 'function' && !!rule(params))
      .map(([className]) => className);
    return classNames.length > 0 ? classNames.join(' ') : undefined;
  };

  const renderEditor = () => {
    const tableRow = editingCell
      ? table.getCoreRowModel().rowsById[editingCell.rowId]
      : undefined;
    const leaf = editingCell ? leafById.get(editingCell.colId) : undefined;
    const editor = leaf ? metaOf(leaf).editor : undefined;
    if (!editingCell || !tableRow || !leaf || !editor) {
      return null;
    }
    const Editor = editor.component;
    const value = tableRow.getValue(editingCell.colId);
    return (
      <CellEditorPopup
        anchor={editingCell.anchor}
        onCancel={() => stopEditing(true)}
      >
        <Editor
          {...editor.params}
          value={value}
          initialValue={value}
          data={tableRow.original}
          node={getBodyNode(tableRow)}
          column={columnShims.get(editingCell.colId)}
          colDef={metaOf(leaf).colDef}
          api={api}
          context={bridge.context}
          rowIndex={tableRow.index}
          eventKey={null}
          cellStartedEdit
          onValueChange={(next: unknown) => {
            editorValueRef.current = { value: next };
          }}
          // AG's editor stopEditing keeps the value (its argument only says
          // whether to move on to the next cell)
          stopEditing={() => stopEditing(false)}
        />
      </CellEditorPopup>
    );
  };

  // What every cell is drawn with besides its own row and column: one object
  // while none of it changes, so the memoised rows can skip re-rendering
  const cellEnv = useMemo(
    (): CellEnv => ({
      getNode,
      columnShims,
      api,
      context: bridge.context,
      rowHeight,
      expanderColumnId: getSubRows ? leafColumns[0]?.id : undefined,
      refreshKey: [sorting, redrawKey],
    }),
    [
      api,
      bridge,
      columnShims,
      getNode,
      getSubRows,
      leafColumns,
      redrawKey,
      rowHeight,
      sorting,
    ],
  );

  // A group whose columns are all pinned to one side sticks with them (its
  // label stays over them, as AG draws a pinned group), from its first
  // column's offset when pinned left, its last column's when pinned right
  const getGroupPin = (
    column: TableColumn,
  ): { side: 'left' | 'right'; style: CSSProperties } | undefined => {
    const leaves = column
      .getLeafColumns()
      .filter((leaf) => !metaOf(leaf).isHidden);
    const side = leaves.length > 0 ? metaOf(leaves[0]).pinned : undefined;
    if (!side || leaves.some((leaf) => metaOf(leaf).pinned !== side)) {
      return undefined;
    }
    const edge = side === 'left' ? leaves[0] : leaves[leaves.length - 1];
    const offset = pinnedOffsets[edge.id];
    if (!offset || isMeasuring) {
      return { side, style: {} };
    }
    return {
      side,
      style: side === 'left' ? { left: offset.left } : { right: offset.right },
    };
  };

  const renderHeader = ({
    header,
    rowSpan,
  }: {
    header: TableHeader;
    rowSpan?: number;
  }) => {
    const { column } = header;
    const meta = metaOf(column);
    const isLeaf = !column.columns || column.columns.length === 0;

    const HeaderComponent = meta.headerComponent;
    const sort = isLeaf ? column.getIsSorted() : false;
    const canSort = isLeaf && !HeaderComponent && column.getCanSort();
    const shim = columnShims.get(column.id);
    const groupPin = isLeaf ? undefined : getGroupPin(column);
    const pinned = isLeaf ? meta.pinned : groupPin?.side;
    const isColumnResizable = !!isResizable && isLeaf;
    const isMovable = isColumnMovable && meta.isMovable;
    const isDragged = columnDrag?.columnId === column.id;
    const classNames = [
      meta.headerClass,
      canSort ? 'dt-sortable' : undefined,
      !isLeaf ? 'dt-group' : undefined,
      pinned ? `dt-pinned dt-pinned-${pinned}` : undefined,
      isColumnResizable ? 'dt-resizable' : undefined,
      isMovable ? 'dt-movable' : undefined,
      isDragged ? 'dt-dragging' : undefined,
      isDragged && columnDrag?.isOutside ? 'dt-drag-hide' : undefined,
    ].filter(Boolean);
    const toggleSorting = canSort
      ? column.getToggleSortingHandler()
      : undefined;

    const leafIndex = leafIndexes.get(column.id);
    return (
      <th
        key={header.id}
        role="columnheader"
        colSpan={header.colSpan}
        rowSpan={rowSpan}
        data-col-id={isLeaf ? column.id : undefined}
        data-group-id={isLeaf ? undefined : column.id}
        className={classNames.join(' ')}
        style={isLeaf ? columnStyles.get(column.id) : groupPin?.style}
        title={meta.headerTooltip}
        aria-sort={
          isLeaf && column.getCanSort()
            ? sort === 'asc'
              ? 'ascending'
              : sort === 'desc'
                ? 'descending'
                : 'none'
            : undefined
        }
        {...(isLeaf && leafIndex !== undefined
          ? nav.getCellProps({ row: 0, col: leafIndex })
          : {})}
        ref={(element) => {
          if (isLeaf && element) {
            headerCellRefs.current.set(column.id, element);
          } else if (!isLeaf) {
            if (element) {
              groupHeaderRefs.current.set(column.id, element);
            } else {
              groupHeaderRefs.current.delete(column.id);
            }
          }
        }}
        onPointerDown={
          isMovable ? (event) => startColumnDrag({ event, column }) : undefined
        }
        // A drag (or resize) ends with a click, which mustn't reach a header
        // component's own click either (its sorting, or a drill-down)
        onClickCapture={(event) => {
          if (isResizingRef.current) {
            event.stopPropagation();
          }
        }}
        onClick={
          toggleSorting
            ? (event) => {
                if (!isResizingRef.current) {
                  toggleSorting(event);
                }
              }
            : undefined
        }
      >
        {HeaderComponent ? (
          <HeaderComponent
            {...meta.headerComponentParams}
            displayName={column.columnDef.header}
            column={shim}
            api={api}
            context={bridge.context}
            enableSorting={column.getCanSort()}
            setSort={(direction: 'asc' | 'desc' | null, isMulti?: boolean) =>
              direction
                ? column.toggleSorting(direction === 'desc', isMulti)
                : column.clearSorting()
            }
            progressSort={(isMulti?: boolean) =>
              column.toggleSorting(undefined, isMulti)
            }
          />
        ) : (
          <DefaultHeader
            label={`${column.columnDef.header ?? ''}`}
            sort={sort}
            isRightAligned={meta.isRightAligned}
            isCentered={!isLeaf}
          />
        )}
        {isColumnResizable && (
          <span
            className="dt-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${column.columnDef.header ?? column.id}`}
            data-testid={`dt-resize-${column.id}`}
            onPointerDown={(event) =>
              startResize({
                event,
                columnId: column.id,
                minWidth: meta.minWidth,
              })
            }
            onClick={(event) => event.stopPropagation()}
          />
        )}
      </th>
    );
  };

  // fitContent and fixed (and fitWidth held by column limits): columns keep
  // their own width and an empty column takes the rest, before any
  // right-pinned columns, as AG lays out a grid that doesn't fill its width
  const hasFiller =
    sizing === 'fitContent' || sizing === 'fixed' || !!limitedLayout;
  const fillerBeforeId = leafColumns.find(
    (c) => metaOf(c).pinned === 'right',
  )?.id;
  const withFiller = ({
    cells,
    columnIds,
    filler,
  }: {
    cells: React.ReactNode[];
    columnIds: string[];
    filler: React.ReactNode;
  }) =>
    hasFiller
      ? insertFiller({ cells, columnIds, filler, fillerBeforeId })
      : cells;

  // A column outside any group spans every header row (TanStack gives it
  // placeholders above), as AG draws it
  const renderHeaderRows = () => {
    const headerGroups = table.getHeaderGroups();
    const drawn = new Set<string>();
    return headerGroups.map((headerGroup, groupIndex) => {
      const headers = headerGroup.headers.filter((header) => {
        const isLeaf =
          !header.column.columns || header.column.columns.length === 0;
        return !(isLeaf && drawn.has(header.column.id));
      });
      const cells = headers.map((header) => {
        const isLeaf =
          !header.column.columns || header.column.columns.length === 0;
        if (header.isPlaceholder && !isLeaf) {
          return (
            <th key={header.id} role="columnheader" colSpan={header.colSpan} />
          );
        }
        if (isLeaf) {
          drawn.add(header.column.id);
        }
        return renderHeader({
          header,
          rowSpan:
            isLeaf && header.isPlaceholder
              ? headerGroups.length - groupIndex
              : undefined,
        });
      });
      return (
        <tr key={headerGroup.id} role="row" aria-rowindex={groupIndex + 1}>
          {groupIndex === 0
            ? withFiller({
                cells,
                // A group header stands for its columns, so the filler goes
                // before a right-pinned group, as it does before the
                // group's first cell in the rows
                columnIds: headers.map(
                  (h) =>
                    h.column
                      .getLeafColumns()
                      .find((leaf) => leaf.id === fillerBeforeId)?.id ??
                    h.column.id,
                ),
                filler: (
                  <th
                    key="dt-filler"
                    className="dt-filler"
                    role="presentation"
                    aria-hidden
                    rowSpan={headerGroups.length}
                  />
                ),
              })
            : cells}
        </tr>
      );
    });
  };

  const headerRowCount = table.getHeaderGroups().length;

  const renderRow = (tableRow: TableRow, displayIndex: number) => (
    <BodyRow
      key={tableRow.id}
      tableRow={tableRow}
      displayIndex={displayIndex}
      isSelected={tableRow.getIsSelected()}
      isExpanded={tableRow.getIsExpanded()}
      headerRowCount={headerRowCount}
      env={cellEnv}
      columnStyles={columnStyles}
      leafIndexes={leafIndexes}
      getCellProps={nav.getCellProps}
      hasFiller={hasFiller}
      fillerBeforeId={fillerBeforeId}
      events={bridge.events}
      onRowClicked={onRowClicked}
      className={getRowClassName(tableRow)}
    />
  );

  const renderBody = () => {
    if (!isVirtualised) {
      return tableRows.map((r, index) => renderRow(r, index));
    }
    if (virtualItems.length === 0) {
      return null;
    }
    const paddingTop = virtualItems[0].start - theadHeight;
    const paddingBottom =
      virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end;
    return (
      <>
        {paddingTop > 0 && (
          <tr role="presentation" aria-hidden style={{ height: paddingTop }}>
            <td
              colSpan={leafColumns.length + (hasFiller ? 1 : 0)}
              style={{ padding: 0 }}
            />
          </tr>
        )}
        {virtualItems.map((item) =>
          renderRow(tableRows[item.index], item.index),
        )}
        {paddingBottom > 0 && (
          <tr role="presentation" aria-hidden style={{ height: paddingBottom }}>
            <td
              colSpan={leafColumns.length + (hasFiller ? 1 : 0)}
              style={{ padding: 0 }}
            />
          </tr>
        )}
      </>
    );
  };

  // The space the rows leave above the totals. Pinned columns keep their cell
  // (and border) so they run down to the totals, as AG's pinned columns do.
  const renderBodyFill = () =>
    bodyFillHeight > 0 && (
      <tr
        className="dt-fill"
        role="presentation"
        aria-hidden
        style={{ height: bodyFillHeight }}
      >
        {withFiller({
          columnIds: leafColumns.map((column) => column.id),
          filler: <td key="dt-filler" className="dt-filler" />,
          cells: leafColumns.map((column) => {
            const { pinned } = metaOf(column);
            return (
              <td
                key={column.id}
                className={pinned ? `dt-pinned dt-pinned-${pinned}` : undefined}
                style={columnStyles.get(column.id)}
              />
            );
          }),
        })}
      </tr>
    );

  if (isLoading && rows.length === 0) {
    return (
      <FlexCentered style={{ height: '100%' }}>
        <Loading />
      </FlexCentered>
    );
  }

  if (noDataToDisplay && !isLoading) {
    return (
      <FlexCentered style={{ height: '100%' }}>
        <NoDataToDisplay size={'normal'} />
      </FlexCentered>
    );
  }

  return (
    <Scroller
      ref={scrollerRef}
      id={tableId}
      layout={scrollerLayout}
      hasBorders={!!hasBorders}
      data-testid={testId}
      // Keyboard focus scrolls a cell clear of the sticky header
      style={{ scrollPaddingTop: theadHeight }}
    >
      <Table
        ref={tableElementRef}
        minWidth={minTableWidth}
        isClickable={!!onRowClicked}
        isMeasuring={isMeasuring}
        hasDarkHeader={!!hasDarkHeader}
        isSearchScreen={!!isSearchScreen}
        // Loading with no rows yet returned the plain spinner above, so
        // here it's a reload of the rows on show
        isReloading={!!isLoading}
        aria-busy={!!isLoading}
        role="grid"
        aria-rowcount={
          headerRowCount +
          (isVirtualised && totalRowCount !== undefined
            ? totalRowCount
            : tableRows.length) +
          totalsRows.length
        }
        aria-colcount={leafColumns.length}
        onKeyDown={onTableKeyDown}
        onFocus={nav.onFocus}
        onClick={onTableClick}
        onContextMenu={onTableContextMenu}
        isHeaderSkippedWhenMeasuring={isMeasuring && !!skipHeaderWhenSizing}
      >
        <thead ref={theadRef}>{renderHeaderRows()}</thead>
        <tbody>
          {renderBody()}
          {renderBodyFill()}
        </tbody>
        {totalsRows.length > 0 && (
          <tfoot>
            {totalsRows.map((data, index) => {
              const rowId = `pinned-${index}`;
              return (
                <tr
                  key={rowId}
                  role="row"
                  data-pinned-index={index}
                  aria-rowindex={headerRowCount + tableRows.length + index + 1}
                >
                  {withFiller({
                    columnIds: leafColumns.map((column) => column.id),
                    filler: (
                      <td
                        key="dt-filler"
                        className="dt-filler"
                        role="presentation"
                        aria-hidden
                      />
                    ),
                    cells: leafColumns.map((column, columnIndex) => {
                      const meta = metaOf(column);
                      return (
                        <td
                          key={column.id}
                          role="gridcell"
                          data-col-id={column.id}
                          {...nav.getCellProps({
                            row: tableRows.length + index + 1,
                            col: columnIndex,
                          })}
                          className={[
                            meta.cellClass,
                            meta.pinned
                              ? `dt-pinned dt-pinned-${meta.pinned}`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={columnStyles.get(column.id)}
                        >
                          {renderCellContent({
                            env: cellEnv,
                            column,
                            data,
                            rowId,
                            rowIndex: index,
                            isPinned: true,
                            value: column.accessorFn
                              ? column.accessorFn(data, index)
                              : undefined,
                          })}
                        </td>
                      );
                    }),
                  })}
                </tr>
              );
            })}
          </tfoot>
        )}
      </Table>
      {isLoading && (
        <Overlay>
          <Loading />
        </Overlay>
      )}
      {renderEditor()}
      {columnDrag && (
        <ColumnDragGhost
          label={columnDrag.label}
          isHiding={columnDrag.isOutside}
          start={columnDrag.start}
        />
      )}
      {menu && (
        <DataTableContextMenu
          x={menu.x}
          y={menu.y}
          entries={menu.entries}
          onClose={closeMenu}
        />
      )}
    </Scroller>
  );
};

// A parent's re-render (a dashboard re-laying out its cards as the window
// resizes) skips the table when its props haven't changed, as AG's grid
// skips its cells. Owners pass stable props (memoised rows, defs, callbacks).
export default React.memo(DataTable);
